import React from 'react';
import type {
  WorkspaceCapabilities,
  WorkspacePolicy,
  WorkspaceResourceRange,
} from '../client';
import './workspace-resources.css';

export interface WorkspaceAllocationFloor {
  cpuMillis?: number;
  memoryMiB?: number;
  storageGiB?: number;
  gpuCount?: number;
}

interface WorkspacePolicySettingsProps {
  policy: WorkspacePolicy;
  capabilities: WorkspaceCapabilities;
  existingAllocationFloor?: WorkspaceAllocationFloor;
  onChange: (policy: WorkspacePolicy) => void;
  disabled?: boolean;
}

interface RangeRowProps {
  id: string;
  label: string;
  value: number;
  range: WorkspaceResourceRange;
  floor?: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
  disabled?: boolean;
}

// Trims a number to at most `maxDecimals` decimal places, dropping trailing
// zeros (e.g. 2.50 -> "2.5", 2.00 -> "2").
export const formatDecimal = (value: number, maxDecimals = 2) =>
  Number(value.toFixed(maxDecimals)).toString();

export const formatCpu = (value: number) =>
  `${formatDecimal(value / 1000)} cores`;
export const formatMemory = (value: number) =>
  `${formatDecimal(value / 1024)} GiB`;
export const formatStorage = (value: number) => `${formatDecimal(value)} GiB`;

const RangeRow: React.FC<RangeRowProps> = ({
  id,
  label,
  value,
  range,
  floor = 0,
  format,
  onChange,
  disabled,
}) => {
  const min = Math.max(range.min, floor);
  const safeValue = Math.min(range.max, Math.max(min, value));
  const listId = `${id}-steps`;
  const middle =
    min + Math.floor((range.max - min) / (2 * range.step)) * range.step;

  return (
    <div className="workspace-range-row">
      <div className="workspace-range-heading">
        <label htmlFor={id}>{label}</label>
        <output htmlFor={id} aria-label={`${label} value`}>
          {format(safeValue)}
        </output>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={range.max}
        step={range.step}
        list={listId}
        value={safeValue}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <datalist id={listId}>
        {[min, middle, range.max].map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </div>
  );
};

export const WorkspacePolicySettings: React.FC<
  WorkspacePolicySettingsProps
> = ({
  policy,
  capabilities,
  existingAllocationFloor = {},
  onChange,
  disabled,
}) => {
  const update = <K extends keyof WorkspacePolicy>(
    key: K,
    value: WorkspacePolicy[K],
  ) => onChange({ ...policy, [key]: value });
  const gpu = capabilities.gpuTypes[0] as
    { minCount?: number; maxCount?: number; step?: number } | undefined;
  const gpuMin = Math.max(
    gpu?.minCount || 1,
    existingAllocationFloor.gpuCount || 0,
  );
  const gpuMax = Math.max(gpuMin, gpu?.maxCount || 0);

  return (
    <>
      <section
        className="workspace-resource-panel"
        aria-labelledby="workspace-default-policy-title">
        <div className="workspace-resource-panel-header">
          <div>
            <h3 id="workspace-default-policy-title">
              Default Workspace Resources
            </h3>
            <p>
              Set resources assigned automatically and prefilled when adding a
              workspace.
            </p>
          </div>
        </div>
        <div className="workspace-range-matrix">
          <RangeRow
            id="workspace-default-cpu"
            label="Default CPU"
            value={policy.defaultCpuMillis}
            range={{ ...capabilities.cpuMillis, max: policy.maxCpuMillis }}
            format={formatCpu}
            disabled={disabled}
            onChange={(value) => update('defaultCpuMillis', value)}
          />
          <RangeRow
            id="workspace-default-memory"
            label="Default memory"
            value={policy.defaultMemoryMiB}
            range={{ ...capabilities.memoryMiB, max: policy.maxMemoryMiB }}
            format={formatMemory}
            disabled={disabled}
            onChange={(value) => update('defaultMemoryMiB', value)}
          />
          <RangeRow
            id="workspace-default-storage"
            label="Default storage"
            value={policy.defaultStorageGiB}
            range={{ ...capabilities.storageGiB, max: policy.maxStorageGiB }}
            format={formatStorage}
            disabled={disabled}
            onChange={(value) => update('defaultStorageGiB', value)}
          />
        </div>
      </section>

      <section
        className="workspace-resource-panel"
        aria-labelledby="workspace-maximum-policy-title">
        <div className="workspace-resource-panel-header">
          <div>
            <h3 id="workspace-maximum-policy-title">
              Maximum Workspace Resources
            </h3>
            <p>
              Set the largest workspace an agent can request from this provider.
            </p>
          </div>
        </div>
        <div className="workspace-range-matrix">
          <RangeRow
            id="workspace-policy-cpu"
            label="Maximum CPU"
            value={policy.maxCpuMillis}
            range={capabilities.cpuMillis}
            floor={existingAllocationFloor.cpuMillis}
            format={formatCpu}
            disabled={disabled}
            onChange={(value) => update('maxCpuMillis', value)}
          />
          <RangeRow
            id="workspace-policy-memory"
            label="Maximum memory"
            value={policy.maxMemoryMiB}
            range={capabilities.memoryMiB}
            floor={existingAllocationFloor.memoryMiB}
            format={formatMemory}
            disabled={disabled}
            onChange={(value) => update('maxMemoryMiB', value)}
          />
          <RangeRow
            id="workspace-policy-storage"
            label="Maximum storage"
            value={policy.maxStorageGiB}
            range={capabilities.storageGiB}
            floor={existingAllocationFloor.storageGiB}
            format={formatStorage}
            disabled={disabled}
            onChange={(value) => update('maxStorageGiB', value)}
          />

          <div className="workspace-gpu-row">
            <label className="workspace-toggle-row workspace-toggle-row-compact">
              <span>
                <strong>GPU</strong>
                <small>
                  {gpu
                    ? 'Allow GPU-backed workspaces.'
                    : 'GPUs are unavailable from this provider'}
                </small>
              </span>
              <input
                type="checkbox"
                aria-label="Allow GPUs"
                checked={policy.allowGpu}
                disabled={disabled || !gpu}
                onChange={(event) =>
                  onChange({
                    ...policy,
                    allowGpu: event.target.checked,
                    maxGpuCount: event.target.checked ? Math.max(gpuMin, 1) : 0,
                  })
                }
              />
            </label>
            {policy.allowGpu && gpu && (
              <label className="workspace-stepper">
                <span>Max GPUs per workspace</span>
                <input
                  type="number"
                  aria-label="Maximum GPUs"
                  min={gpuMin}
                  max={gpuMax}
                  step={gpu.step || 1}
                  value={Math.min(gpuMax, Math.max(gpuMin, policy.maxGpuCount))}
                  disabled={disabled}
                  onChange={(event) =>
                    update('maxGpuCount', Number(event.target.value))
                  }
                />
              </label>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkspacePolicySettings;
